/**
 * OnboardingDashboard — Members hired, no Jobs yet. Same skeleton as Empty
 * with three deltas (spec §5.4):
 *   - Tiles show numeric counts (Team Members real, others '0').
 *   - GetStartedCards uses the "onboarding" copy ("Add another Member").
 *   - StartingMembersGrid is filtered by already-added positions.
 *   - HowItWorksStrip remains.
 */

import { useMemo } from 'react';
import { usePositionsCatalog, useStaffProfiles, useUnitsCatalog } from '@/features/members/hooks';
import { t } from '@/lib/i18n';
import type { Member } from '@/features/members/schemas';
import { DashboardGreeting } from '../components/DashboardGreeting';
import { StatTile } from '../components/StatTile';
import { GetStartedCards } from '../components/GetStartedCards';
import { StartingMembersGrid, filterAlreadyAdded } from '../components/StartingMembersGrid';
import { HowItWorksStrip } from '../components/HowItWorksStrip';
import styles from './OnboardingDashboard.module.css';

export interface OnboardingDashboardProps {
  firstName: string;
  members: Member[];
}

export function OnboardingDashboard({ firstName, members }: OnboardingDashboardProps) {
  const staffProfiles = useStaffProfiles();
  const positions = usePositionsCatalog();
  const units = useUnitsCatalog();
  const allProfiles = staffProfiles.data?.items ?? [];
  const remaining = filterAlreadyAdded(allProfiles, members);
  const positionsById = useMemo(
    () => new Map((positions.data?.items ?? []).map((p) => [p.id, p.name])),
    [positions.data?.items],
  );
  const unitsById = useMemo(
    () => new Map((units.data?.items ?? []).map((u) => [u.id, u.name])),
    [units.data?.items],
  );

  return (
    <div className={styles.root} data-testid="dashboard-state-onboarding">
      <DashboardGreeting state="onboarding" firstName={firstName} />
      <GetStartedCards state="onboarding" />
      <section className={styles.metricsSection}>
        <h2 className={styles.metricsTitle}>{t('Current Activity')}</h2>
        <div className={styles.tileRow}>
          <StatTile
            label={t('Team Members')}
            value={members.length}
            subtitle={t('{n} working now', { n: '0' })}
          />
          <StatTile label={t('Jobs Running')} value={0} subtitle={t('live work in progress')} />
          <StatTile label={t('Need Your Input')} value={0} subtitle={t('members blocked on you')} />
          <StatTile
            label={t('Jobs Completed')}
            value={0}
            subtitle={t('across the team, all time')}
          />
        </div>
      </section>
      <StartingMembersGrid
        profiles={remaining}
        positionsById={positionsById}
        unitsById={unitsById}
      />
      <HowItWorksStrip />
    </div>
  );
}
